import React, { useState, useEffect, useRef, useCallback } from 'react';
import axiosInstance from '../../api/axiosInstance';
import {
  PhoneIcon,
  PhoneArrowUpRightIcon,
  PhoneArrowDownLeftIcon,
  PhoneXMarkIcon,
  ArrowPathIcon,
  SignalIcon,
  UserIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import logger from '../../../utils/logger';
import {
  PageHeader,
  StatCard,
  StatGrid,
  StatusPill,
  EmptyState,
  ConfirmDialog,
  LoadingBlock,
  CARD,
  TEXT_MUTED,
  TEXT_PRIMARY,
  btn,
} from './components';
import { formatPhoneNumber, formatElapsed, asArray } from './components/format';

const POLL_MS = 3000;

/**
 * Live call monitor.
 *
 * Rebuilt on the shared Voice kit: the previous version hand-rolled four KPI
 * cards (57 unrolled lines), its own badge factory, its own spinner and empty
 * state, and used window.confirm() for the two destructive actions.
 */
const ActiveCalls = ({ showNotification }) => {
  const [loading, setLoading] = useState(true);
  const [calls, setCalls] = useState([]);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  // Re-render on a timer so the elapsed durations tick even between polls.
  const [, setTick] = useState(0);
  const intervalRef = useRef(null);

  const fetchCalls = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/api/sip/active-calls');
      if (res.data.success) setCalls(asArray(res.data, 'calls'));
    } catch (error) {
      logger.error('Error fetching active calls:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCalls();
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchCalls, POLL_MS);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoRefresh, fetchCalls]);

  // Durations previously only advanced when a poll returned, so the timer
  // visibly jumped in 3s steps. This ticks it every second independently.
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const runAction = async (fn, successMessage, failureMessage) => {
    setBusy(true);
    try {
      const res = await fn();
      if (res.data.success) {
        showNotification?.(successMessage(res.data), 'success');
        await fetchCalls();
      }
    } catch (error) {
      logger.error(failureMessage, error);
      showNotification?.(error.response?.data?.error || failureMessage, 'error');
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const stats = {
    total: calls.length,
    inbound: calls.filter((c) => c.direction === 'inbound').length,
    outbound: calls.filter((c) => c.direction !== 'inbound').length,
    ringing: calls.filter((c) => ['ringing', 'early'].includes(String(c.state).toLowerCase())).length,
  };

  return (
    <div className="h-full overflow-y-auto bg-gray-50 dark:bg-[#0d0d0d]">
      <PageHeader
        title="Active calls"
        subtitle="Live call monitoring"
        badge={
          stats.total > 0 && (
            <StatusPill
              status="active"
              label={`${stats.total} live`}
              tone={stats.total ? 'danger' : 'neutral'}
            />
          )
        }
        actions={
          <>
            <label className={`flex items-center gap-2 text-[13px] mr-1 cursor-pointer ${TEXT_MUTED}`}>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-orange-600 focus:ring-2 focus:ring-orange-500/40 bg-white dark:bg-[#1e1e1e]"
              />
              Auto-refresh
            </label>
            <button
              onClick={() =>
                setConfirm({
                  title: 'Clean up stale calls',
                  message:
                    'Calls still marked live after 4 hours will be closed. Genuinely active calls are untouched.',
                  confirmLabel: 'Clean up',
                  variant: 'primary',
                  action: () =>
                    runAction(
                      () => axiosInstance.post('/api/sip/active-calls/cleanup'),
                      (d) => {
                        const { cdrs, voiceCalls } = d.cleaned || {};
                        return `Closed ${(cdrs || 0) + (voiceCalls || 0)} stale record(s)`;
                      },
                      'Failed to clean up stale calls'
                    ),
                })
              }
              className={btn('secondary', 'sm')}
            >
              Clean up stale
            </button>
            <button onClick={fetchCalls} className={btn('secondary', 'sm')} aria-label="Refresh">
              <ArrowPathIcon className="w-4 h-4" />
              Refresh
            </button>
          </>
        }
      />

      <div className="p-6 space-y-5 max-w-7xl mx-auto">
        <StatGrid cols={4}>
          <StatCard label="Total active" value={stats.total} icon={PhoneIcon} tone={stats.total ? 'danger' : 'neutral'} />
          <StatCard label="Inbound" value={stats.inbound} icon={PhoneArrowDownLeftIcon} tone="success" />
          <StatCard label="Outbound" value={stats.outbound} icon={PhoneArrowUpRightIcon} tone="info" />
          <StatCard label="Ringing" value={stats.ringing} icon={SignalIcon} tone="warning" />
        </StatGrid>

        {loading ? (
          <LoadingBlock label="Loading active calls…" />
        ) : calls.length === 0 ? (
          <EmptyState
            icon={PhoneIcon}
            title="No active calls"
            description="Live calls appear here as soon as they connect."
          />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {calls.map((call) => (
              <CallCard
                key={call.callId || call._id}
                call={call}
                onHangup={() =>
                  setConfirm({
                    title: 'End this call?',
                    message: `This immediately disconnects both parties on the call with ${formatPhoneNumber(
                      call.direction === 'inbound' ? call.caller?.number : call.callee?.number
                    )}.`,
                    confirmLabel: 'End call',
                    variant: 'danger',
                    action: () =>
                      runAction(
                        () => axiosInstance.post(`/api/sip/active-calls/${call.callId}/hangup`),
                        () => 'Call ended',
                        'Failed to end the call'
                      ),
                  })
                }
              />
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!confirm}
        onCancel={() => setConfirm(null)}
        onConfirm={() => confirm?.action?.()}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        variant={confirm?.variant}
        loading={busy}
      />
    </div>
  );
};

/** One live call. */
const CallCard = ({ call, onHangup }) => {
  const inbound = call.direction === 'inbound';
  const state = String(call.state || '').toLowerCase();
  const ringing = state === 'ringing' || state === 'early';

  const DirIcon = inbound ? PhoneArrowDownLeftIcon : PhoneArrowUpRightIcon;
  const other = inbound ? call.caller : call.callee;
  const mine = inbound ? call.callee : call.caller;

  return (
    <div className={`${CARD} p-4 flex flex-col gap-4`}>
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg flex-shrink-0 ${inbound ? 'bg-emerald-500/10 text-emerald-500' : 'bg-blue-500/10 text-blue-500'}`}>
          <DirIcon className="w-4 h-4" />
        </div>

        <div className="min-w-0 flex-1">
          {/* Lead with the number, not "Outbound Call" — the label was the
              same on every card and the number is what identifies it. */}
          <p className={`text-sm font-semibold truncate tabular-nums ${TEXT_PRIMARY}`}>
            {formatPhoneNumber(other?.number)}
          </p>
          <p className={`text-[12px] truncate ${TEXT_MUTED}`}>
            {other?.name || call.agentName || (inbound ? 'Inbound' : 'Outbound')}
            {mine?.number ? ` · via ${formatPhoneNumber(mine.number)}` : ''}
          </p>
        </div>

        <StatusPill
          status={state || 'active'}
          tone={ringing ? 'warning' : 'success'}
          className={ringing ? 'animate-pulse' : ''}
        />
      </div>

      <div className="flex items-center gap-4 text-[13px]">
        <span className={`font-semibold tabular-nums ${TEXT_PRIMARY}`}>
          {formatElapsed(call.startTime || call.createdAt)}
        </span>
        {call.agentName && (
          <span className={`inline-flex items-center gap-1 ${TEXT_MUTED}`}>
            <SparklesIcon className="w-3.5 h-3.5" />
            {call.agentName}
          </span>
        )}
        {call.quality?.mos != null && (
          <span className={`inline-flex items-center gap-1 ${TEXT_MUTED}`} title="Mean opinion score">
            <SignalIcon className="w-3.5 h-3.5" />
            {Number(call.quality.mos).toFixed(1)}
          </span>
        )}
        {call.credential?.username && (
          <span className={`inline-flex items-center gap-1 truncate ${TEXT_MUTED}`}>
            <UserIcon className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">{call.credential.username}</span>
          </span>
        )}
      </div>

      <button onClick={onHangup} className={`${btn('danger', 'sm')} w-full border-t-0`}>
        <PhoneXMarkIcon className="w-4 h-4" />
        End call
      </button>
    </div>
  );
};

export default ActiveCalls;
